/**
 * Importação assistida de planilha prescrita (PDF/TXT): envio → proposta → revisão → rascunhos.
 * Nada é publicado aqui: os treinos confirmados entram como rascunho na planilha.
 */
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, upload } from "../api/client";
import type { ImportJob, PlanProposalItem, WorkoutType } from "../api/types";
import { fmtLong, WORKOUT_TYPE_LABEL } from "../lib/training";
import { Badge, Button, FormError, Modal, TextField } from "./ui";
import { t, tn, tnj } from "../i18n";

const CONF = { high: { label: t("Confiança alta"), tone: "ok" }, medium: { label: t("Confira"), tone: "warn" }, low: { label: t("Revise"), tone: "danger" } } as const;

export function PlanImportModal({
  open,
  base,
  defaultMonday,
  onClose,
  onApplied,
}: {
  open: boolean;
  base: string;
  defaultMonday: string;
  onClose: () => void;
  onApplied: (count: number, firstDate: string | null) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [monday, setMonday] = useState(defaultMonday);
  const [job, setJob] = useState<ImportJob | null>(null);
  const [items, setItems] = useState<PlanProposalItem[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setJob(null);
      setItems([]);
      setError(null);
      setMonday(defaultMonday);
    }
  }, [open, defaultMonday]);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      if (monday) form.append("reference_monday", monday);
      let j = await upload<ImportJob>(`${base}/imports`, form);
      for (let i = 0; i < 30 && (j.status === "queued" || j.status === "processing"); i++) {
        await new Promise((r) => setTimeout(r, 1000));
        j = await api.get<ImportJob>(`${base}/imports/${j.id}`);
      }
      setJob(j);
      setItems(j.result.proposal?.items ?? []);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const update = (i: number, patch: Partial<PlanProposalItem>) =>
    setItems((prev) => prev.map((it, k) => (k === i ? { ...it, ...patch } : it)));
  const chosen = items.filter((it) => it.include);
  const missingDate = chosen.some((it) => !it.date);

  async function confirm() {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ sessions_created: number; first_date: string | null }>(`${base}/imports/${job.id}/confirm`, {
        items: chosen.map(({ date, title, workout_type, structure, instructions }) => ({ date, title, workout_type, structure, instructions })),
      });
      onApplied(r.sessions_created, r.first_date);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (job?.status === "needs_review") await api.post(`${base}/imports/${job.id}/discard`).catch(() => undefined);
    onClose();
  }

  const reviewing = job?.status === "needs_review";
  return (
    <Modal
      open={open}
      wide
      title={reviewing ? t("Revisar planilha importada") : t("Importar planilha (PDF ou TXT)")}
      onClose={reviewing ? discard : onClose}
      footer={
        reviewing ? (
          <>
            <Button onClick={discard}>{t("Descartar")}</Button>
            <Button variant="primary" onClick={confirm} busy={busy} disabled={!chosen.length || missingDate}>
              {tn("Criar {n} treino como rascunho", "Criar {n} treinos como rascunho", chosen.length)}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>{t("Cancelar")}</Button>
            <Button variant="primary" type="submit" form="plan-import" busy={busy} disabled={!file}>
              {t("Ler arquivo")}
            </Button>
          </>
        )
      }
    >
      <FormError error={error} />
      {!reviewing && (
        <form id="plan-import" className="form" onSubmit={send}>
          <p className="muted">
            {t("O EAPace lê o texto, sugere data, tipo e estrutura de cada treino e mostra tudo para você revisar. Só depois da sua confirmação os treinos entram como rascunho — o atleta não vê nada até você publicar.")}
          </p>
          <div className="field">
            <label htmlFor="plan-file">{t("Arquivo")}</label>
            <input id="plan-file" type="file" accept=".pdf,.txt" className="input" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <span className="hint">{t("PDF com texto selecionável ou TXT. Linhas começando com a data (12/10) ou o dia da semana (Segunda, Ter…).")}</span>
          </div>
          <TextField
            label={t("Segunda-feira da primeira semana")}
            type="date"
            value={monday}
            onChange={(e) => setMonday(e.target.value)}
            hint={t("Usada quando o documento traz só os dias da semana, sem datas.")}
          />
          {job?.status === "failed" && (
            <div className="alert alert-error" role="alert">
              <strong>{job.error_message}</strong>
            </div>
          )}
        </form>
      )}
      {reviewing && (
        <div className="stack">
          <p>
            {tnj(
              "{n} dia reconhecido em {file}. Desmarque o que não quiser, ajuste data, título e tipo. A estrutura em blocos pode ser refinada depois, no próprio treino.",
              "{n} dias reconhecidos em {file}. Desmarque o que não quiser, ajuste data, título e tipo. A estrutura em blocos pode ser refinada depois, no próprio treino.",
              items.length,
              { file: <strong>{job?.file?.name}</strong> },
            )}
          </p>
          {job?.result.proposal?.needs_reference_week && (
            <div className="alert alert-info">{t("O documento não tem datas. Volte, informe a segunda-feira da primeira semana e leia de novo.")}</div>
          )}
          <div className="table-wrap">
            <table className="data proposal">
              <thead>
                <tr>
                  <th>{t("Usar")}</th>
                  <th>{t("Data")}</th>
                  <th>{t("Título e tipo")}</th>
                  <th>{t("Texto original")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className={it.include ? "" : "is-off"}>
                    <td>
                      <input type="checkbox" aria-label={t("Usar {title}", { title: it.title })} checked={it.include} onChange={(e) => update(i, { include: e.target.checked })} />
                    </td>
                    <td>
                      <input
                        type="date"
                        className="input"
                        aria-label={t("Data")}
                        value={it.date ?? ""}
                        onChange={(e) => update(i, { date: e.target.value || null })}
                      />
                      {it.date && <span className="sub">{fmtLong(it.date)}</span>}
                    </td>
                    <td>
                      <input className="input" aria-label={t("Título")} value={it.title} maxLength={120} onChange={(e) => update(i, { title: e.target.value })} />
                      <select
                        className="select"
                        aria-label={t("Tipo")}
                        value={it.workout_type}
                        onChange={(e) => update(i, { workout_type: e.target.value as WorkoutType })}
                      >
                        {Object.entries(WORKOUT_TYPE_LABEL).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <span className="proposal-meta">
                        <Badge tone={CONF[it.confidence].tone}>{CONF[it.confidence].label}</Badge>
                        {it.structure.length > 0 && <span className="muted">{tn("{n} bloco reconhecido", "{n} blocos reconhecidos", it.structure.length)}</span>}
                      </span>
                      {it.warnings.map((w) => (
                        <span key={w} className="sub">
                          {w}
                        </span>
                      ))}
                    </td>
                    <td className="raw">{it.raw_text || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {missingDate && <p className="error">{t("Informe a data de todos os treinos marcados.")}</p>}
        </div>
      )}
    </Modal>
  );
}
